import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../schema.dexie";
import { getConfig, getCriancaAtiva, salvarConfig } from "../db/queries";
import { restaurarDeSqlite } from "../lib/exportSqlite";
import { DEMO_FLAG, DEMO_VERSION, semearDemo } from "../seedDemo";
import { CONFIG_PADRAO, REFEICAO_LABEL, type Refeicao } from "../types";
import { idade } from "../lib/format";

const REFEICOES: Refeicao[] = ["cafe", "almoco", "lanche", "jantar"];

export function Config() {
  const nav = useNavigate();
  const cfg = useLiveQuery(getConfig);
  const crianca = useLiveQuery(getCriancaAtiva);
  const [restaurando, setRestaurando] = useState(false);
  const [msgRestore, setMsgRestore] = useState<string | null>(null);

  async function apagarTudo() {
    const ok = window.confirm(
      "apagar todos os dados deste aparelho? esta ação não pode ser desfeita.",
    );
    if (!ok) return;
    await db.delete();
    window.location.reload();
  }

  async function restaurarBackup(file: File) {
    setRestaurando(true);
    setMsgRestore(null);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const r = await restaurarDeSqlite(bytes);
      setMsgRestore(
        `restaurado ✓ — ${r.alimentos} alimentos, ${r.ofertas} ofertas, ${r.experiencias} experiências, ${r.momentos} momentos, ${r.atividades} atividades, ${r.momentosPsm} momentos de corpo, ${r.profissionais} profissionais, ${r.notas} notas, ${r.fotos} fotos`,
      );
    } catch {
      setMsgRestore("não foi possível ler este arquivo .sqlite");
    } finally {
      setRestaurando(false);
    }
  }

  const anos = idade(crianca?.data_nascimento ?? null);
  const qtd = cfg?.escolha_qtd_por_refeicao ?? CONFIG_PADRAO.escolha_qtd_por_refeicao;

  function setQtd(r: Refeicao, n: number) {
    salvarConfig({
      escolha_qtd_por_refeicao: { ...qtd, [r]: Math.max(1, n) },
    });
  }

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav(-1)}>
        ← voltar
      </button>
      <h1 className="tela-titulo">configurações</h1>

      <section className="secao">
        <h2 className="secao-titulo">paciente e responsável</h2>
        <button
          className="card linha-alimento"
          onClick={() => nav("/paciente")}
          style={{ width: "100%" }}
        >
          <div className="foto" aria-hidden>
            👤
          </div>
          <div className="crescer">
            {crianca ? (
              <>
                <div className="nome">
                  {crianca.nome_completo || crianca.apelido}
                  {anos !== null && (
                    <span className="meta"> · {anos} {anos === 1 ? "ano" : "anos"}</span>
                  )}
                </div>
                <div className="meta">
                  {crianca.responsavel_nome
                    ? `responsável: ${crianca.responsavel_nome}`
                    : "toque para completar o responsável"}
                </div>
              </>
            ) : (
              <>
                <div className="nome">completar cadastro</div>
                <div className="meta">paciente e responsável</div>
              </>
            )}
          </div>
          <span aria-hidden>→</span>
        </button>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">lembretes</h2>
        <div className="card">
          <div className="campo" style={{ margin: 0 }}>
            <label>reoferecer um alimento em teste a cada (dias)</label>
            <input
              type="number"
              min={1}
              max={30}
              value={cfg?.reexposicao_dias ?? 5}
              onChange={(e) => salvarConfig({ reexposicao_dias: Number(e.target.value) })}
            />
          </div>
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">criança</h2>
        <button className="btn btn-bloco btn-grande" onClick={() => nav("/refeicao")}>
          🍽️ preparar refeição
        </button>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">enviar pedido por WhatsApp</h2>
        <div className="card">
          <div className="campo" style={{ margin: 0 }}>
            <label>número fixo (opcional)</label>
            <input
              type="tel"
              inputMode="tel"
              placeholder="ex.: 55 11 99999-9999"
              value={cfg?.whatsapp_destino ?? ""}
              onChange={(e) =>
                salvarConfig({ whatsapp_destino: e.target.value.trim() || null })
              }
            />
            <p style={{ color: "var(--text-muted)", fontSize: "0.8rem", margin: "6px 0 0" }}>
              com DDI + DDD. vazio = abre o WhatsApp pra você escolher o contato.
            </p>
          </div>
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">itens que a criança escolhe por refeição</h2>
        <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginTop: 0 }}>
          quantos itens a criança pode escolher do prato montado, em cada refeição.
        </p>
        <div className="card">
          <div className="linha-campos">
            {REFEICOES.map((r) => (
              <div className="campo" style={{ margin: 0 }} key={r}>
                <label>{REFEICAO_LABEL[r]}</label>
                <input
                  type="number"
                  min={1}
                  max={6}
                  value={qtd[r]}
                  onChange={(e) => setQtd(r, Number(e.target.value))}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">backup</h2>
        <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginTop: 0 }}>
          gere o arquivo .sqlite no relatório. aqui você restaura um backup (mescla com
          o que já existe, sem apagar).
        </p>
        <label
          className="btn btn-bloco btn-grande"
          style={{ cursor: restaurando ? "default" : "pointer" }}
        >
          {restaurando ? "restaurando…" : "↩️ restaurar backup (.sqlite)"}
          <input
            type="file"
            accept=".sqlite,.db,application/x-sqlite3,application/octet-stream"
            hidden
            disabled={restaurando}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void restaurarBackup(f);
              e.target.value = "";
            }}
          />
        </label>
        {msgRestore && (
          <p style={{ textAlign: "center", color: "var(--accent-forte)", marginTop: "var(--s2)" }}>
            {msgRestore}
          </p>
        )}
      </section>

      <section className="secao">
        <h2 className="secao-titulo">privacidade</h2>
        <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
          tudo fica neste aparelho, sem conta. os dados só saem quando você exporta um
          relatório.
        </p>
        <button
          className="btn btn-bloco"
          style={{ color: "var(--st-recusado-fg)" }}
          onClick={apagarTudo}
        >
          apagar todos os dados
        </button>
      </section>

      {import.meta.env.DEV && (
        <section className="secao">
          <h2 className="secao-titulo">desenvolvimento</h2>
          <button
            className="btn btn-bloco"
            onClick={async () => {
              if (!window.confirm("apagar tudo e recarregar a massa de demonstração?")) return;
              await semearDemo();
              localStorage.setItem(DEMO_FLAG, DEMO_VERSION);
              window.location.reload();
            }}
          >
            🧪 recarregar dados de demonstração
          </button>
          <p style={{ color: "var(--text-suave)", fontSize: "0.8rem", margin: "var(--s2) 0 0" }}>
            só em dev: limpa o banco e repopula o cenário fictício dos 3 módulos.
          </p>
        </section>
      )}
    </main>
  );
}
